// useBoqCustomSubtradeReview.js
//
// Phase 4 / 4+ admin template editor hook. Reads the
// boq.subtrade_review_overview view (every subtrade in brio_trade, canonical
// + custom, joined to SD template-line metadata and live project_items usage)
// and exposes the five admin actions:
//
//   - updateSubtrade(nodeId, fields)   → boq.update_subtrade(...)
//   - deleteSubtrade(nodeId)           → boq.delete_subtrade(...)
//   - promoteSubtrade(nodeId)          → boq.promote_custom_subtrade(...)
//   - mergeSubtrade(src, target)       → boq.merge_custom_subtrade(...)
//   - rejectSubtrade(nodeId, reason)   → boq.reject_custom_subtrade(...)
//
// All five RPCs are SECURITY DEFINER and gated server-side to admins /
// supervisors, so the frontend doesn't double-check the role.

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { formatSupabaseError } from '../../hooks/useSupabase';

const mapRow = (r) => ({
  nodeId: r.node_id,
  code: r.code,
  subtradeLabel: r.subtrade_label,
  tradeNodeId: r.trade_node_id,
  tradeLabel: r.trade_label,
  tradeSortOrder: r.trade_sort_order || 0,
  isCustom: r.is_custom,
  proposedBy: r.proposed_by,
  proposedAt: r.proposed_at,
  templateLineId: r.template_line_id,
  defaultUnit: r.default_unit,
  defaultLineType: r.default_line_type,
  templateSortOrder: r.template_sort_order || 0,
  isCore: r.is_core,
  note: r.note,
  usageCount: r.usage_count || 0,
  distinctProjects: r.distinct_projects || 0,
});

export default function useBoqCustomSubtradeReview() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .schema('boq')
        .from('subtrade_review_overview')
        .select('node_id, code, subtrade_label, trade_node_id, trade_label, trade_sort_order, is_custom, proposed_by, proposed_at, template_line_id, default_unit, default_line_type, template_sort_order, is_core, note, usage_count, distinct_projects')
        .order('trade_sort_order', { ascending: true })
        .order('template_sort_order', { ascending: true })
        .order('subtrade_label', { ascending: true });
      if (err) throw err;
      setRows((data || []).map(mapRow));
    } catch (err) {
      console.error('[useBoqCustomSubtradeReview] fetch failed', err);
      setError(formatSupabaseError(err, { action: 'fetch', entity: 'subtrade overview' }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const runRpc = useCallback(async (name, args, entity) => {
    setSubmitting(true);
    try {
      const { data, error: err } = await supabase.schema('boq').rpc(name, args);
      if (err) throw err;
      await fetchAll();
      return { success: true, result: data };
    } catch (err) {
      console.error(`[useBoqCustomSubtradeReview] ${name} failed`, err);
      return {
        success: false,
        error: formatSupabaseError(err, { action: name.replace(/_subtrade$/, ''), entity }),
      };
    } finally {
      setSubmitting(false);
    }
  }, [fetchAll]);

  const addSubtrade = useCallback(
    (tradeLabel, { label, defaultUnit, defaultLineType, sortOrder, note, isCustom = false } = {}) =>
      runRpc('create_subtrade', {
        p_trade_label: tradeLabel,
        p_subtrade_label: label,
        ...(defaultUnit ? { p_default_unit: defaultUnit } : {}),
        ...(defaultLineType ? { p_default_line_type: defaultLineType } : {}),
        ...(sortOrder !== undefined && sortOrder !== null ? { p_sort_order: sortOrder } : {}),
        ...(note ? { p_note: note } : {}),
        p_is_custom: isCustom,
      }, 'subtrade'),
    [runRpc]
  );

  const updateSubtrade = useCallback(
    (nodeId, { label, defaultUnit, defaultLineType, sortOrder, note } = {}) =>
      runRpc('update_subtrade', {
        p_node_id: nodeId,
        ...(label !== undefined ? { p_new_label: label } : {}),
        ...(defaultUnit !== undefined ? { p_new_default_unit: defaultUnit } : {}),
        ...(defaultLineType !== undefined ? { p_new_default_line_type: defaultLineType } : {}),
        ...(sortOrder !== undefined ? { p_new_sort_order: sortOrder } : {}),
        ...(note !== undefined ? { p_new_note: note } : {}),
      }, 'subtrade'),
    [runRpc]
  );

  const deleteSubtrade = useCallback(
    (nodeId) => runRpc('delete_subtrade', { p_node_id: nodeId }, 'subtrade'),
    [runRpc]
  );

  const promoteSubtrade = useCallback(
    (nodeId) => runRpc('promote_custom_subtrade', { p_node_id: nodeId }, 'custom subtrade'),
    [runRpc]
  );

  const mergeSubtrade = useCallback(
    (nodeId, targetNodeId) =>
      runRpc('merge_custom_subtrade',
        { p_node_id: nodeId, p_target_node_id: targetNodeId },
        'custom subtrade'),
    [runRpc]
  );

  const rejectSubtrade = useCallback(
    (nodeId, reason) =>
      runRpc('reject_custom_subtrade',
        { p_node_id: nodeId, p_reason: reason || null },
        'custom subtrade'),
    [runRpc]
  );

  const reorderSubtrades = useCallback(
    (nodeIds) => runRpc('reorder_subtrades', { p_node_ids: nodeIds }, 'subtrade order'),
    [runRpc]
  );

  return {
    rows, loading, submitting, error,
    refresh: fetchAll,
    addSubtrade, updateSubtrade, deleteSubtrade,
    promoteSubtrade, mergeSubtrade, rejectSubtrade,
    reorderSubtrades, setRows,
  };
}
